import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { CarState } from '../sim/car';
import type { Ground } from '../sim/ground';

/** Smooth chase camera that sits behind and above the taxi and looks a little ahead of it. */
export class ChaseCamera {
  readonly camera: UniversalCamera;
  private pos = new Vector3();
  private look = new Vector3();
  private shake = 0;
  distance = 8.5;
  height = 3.4;
  lookAhead = 7;

  ground: Ground | null = null;
  constructor(scene: Scene) {
    this.camera = new UniversalCamera('chase', new Vector3(0, 5, -10), scene);
    this.camera.fov = 0.95;
    this.camera.minZ = 0.3;
    this.camera.maxZ = 1200;
    this.camera.inputs.clear();
    scene.activeCamera = this.camera;
  }

  snapTo(car: CarState): void {
    const fx = Math.sin(car.yaw), fz = Math.cos(car.yaw);
    this.pos.set(car.x - fx * this.distance, car.y + this.height, car.z - fz * this.distance);
    this.look.set(car.x + fx * this.lookAhead, car.y + 1, car.z + fz * this.lookAhead);
    this.camera.position.copyFrom(this.pos);
    this.camera.setTarget(this.look);
  }

  addShake(amount: number): void { this.shake = Math.min(1.5, this.shake + amount); }

  update(car: CarState, dt: number): void {
    const fx = Math.sin(car.yaw), fz = Math.cos(car.yaw);
    // pull back a touch at speed
    let d = this.distance + Math.min(3, Math.abs(car.speed) * 0.08);
    // occlusion: shorten the boom until nothing tall stands between the cab and the camera
    if (this.ground) {
      for (let k = 0; k < 6; k++) {
        const cx = car.x - fx * d, cz = car.z - fz * d;
        const tall = this.ground.blockedAt(cx, cz) && this.ground.heightAt(cx, cz) + this.ground.topAt(cx, cz) > car.y + 2.6;
        if (!tall) break;
        d *= 0.72;
      }
    }
    const tx = car.x - fx * d, ty = car.y + this.height + Math.min(1, Math.abs(car.speed) * 0.02) - (this.distance - d) * 0.15, tz = car.z - fz * d;
    const k = Math.min(1, 5 * dt);
    this.pos.x += (tx - this.pos.x) * k; this.pos.y += (ty - this.pos.y) * k * 1.4; this.pos.z += (tz - this.pos.z) * k;
    const lk = Math.min(1, 8 * dt);
    this.look.x += (car.x + fx * this.lookAhead - this.look.x) * lk;
    this.look.y += (car.y + 1 - this.look.y) * lk;
    this.look.z += (car.z + fz * this.lookAhead - this.look.z) * lk;
    if (this.shake > 0) {
      this.shake = Math.max(0, this.shake - dt * 2.5);
      const s = this.shake * 0.25;
      this.camera.position.set(this.pos.x + (Math.random() - 0.5) * s, this.pos.y + (Math.random() - 0.5) * s, this.pos.z + (Math.random() - 0.5) * s);
    } else this.camera.position.copyFrom(this.pos);
    this.camera.setTarget(this.look);
  }
}
